import React, { useState } from 'react';
import { format, isSameDay } from 'date-fns';

import Link from '@dashboard/components/Link';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';

import { formatPrice } from '@dashboard/services/formatting';
import { confirm } from '@/dashboard/lib/confirm';

import MultilineText from '@dashboard/components/MultilineText';
import Spinner from '@dashboard/components/Spinner';
import getCouponDiscount from '@dashboard/services/getCouponDiscount';
import useLicenseKeyQueries from '@dashboard/hooks/queries/useLicenseKeyQueries';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import {
  ClipboardIcon,
  EyeIcon,
  EnvelopeIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import PropertyList from '@dashboard/components/PropertyList';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Tooltip, { InfoTooltip } from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { isEmpty, toLower } from 'lodash';
import renderFieldValue from '@dashboard/services/pageFieldTypes/renderFieldValue';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getPaymentMethodLabel from '@dashboard/services/getPaymentMethodLabel';
import Callout from '@dashboard/components/Callout';
import isSubscriptionSuccessful from '@dashboard/services/status/isSubscriptionSuccesful';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import Button from '@dashboard/components/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@dashboard/context/ToastContext';
import { fetcher } from '@dashboard/services/request';
import { subscriptionKey } from '@dashboard/lib/queryKeys';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import PaymentsSubscriptionsCancelDialog from '../components/PaymentsSubscriptionsCancelDialog';
import { formatDateTime } from '@/dashboard/lib/format-date';

function getAmountToBeDiscounted({ subscriptionAmount = 0, fees = [] }) {
  // subscriptionAmount is total recurring amount including fees, before coupon discount
  // fees aren't discounted so need to be subtracted
  // setup fee may be added if coupon applies to setup fee
  let amount = subscriptionAmount;

  if (fees.length > 0) {
    amount -= fees.reduce((acc, fee) => acc + fee.amount, 0);
  }

  return amount;
}

const PaymentsSubscriptionsDetail = ({ subscription, refetch }) => {
  const { seller, isAdminLoggedInAsSeller, token } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const { toggleLicenseKey } = useLicenseKeyQueries();
  const [isTogglingLicenseKey, setIsTogglingLicenseKey] = useState(false);

  const queryClient = useQueryClient();
  const { emitToast } = useToast();

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: async values => {
      return await fetcher(`/sellers/${seller._id}/subscriptions/${subscription._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: values }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKey(subscription._id) });

      emitToast({
        title: 'Fields updated',
      });
    },
  });

  const handleSendSubscriptionConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubscriptionConfirmationEmail();
    }
  };

  const {
    mutateAsync: sendSubscriptionConfirmationEmail,
    isLoading: isSendingSubscriptionConfirmationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${seller._id}/subscriptions/${subscription._id}/send-confirmation-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      emitToast({
        title: 'Confirmation email resent',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to send confirmation email',
        type: 'error',
      });
    },
  });

  React.useEffect(() => {
    if (subscription?.seller) {
      setAdminGhostingSellerId(subscription.seller);
    }
  }, [subscription?.seller, setAdminGhostingSellerId]);

  /**
   * @param {'enable'|'disable'} action
   */
  async function handleToggleLicenseKey() {
    setIsTogglingLicenseKey(true);

    const action = subscription.licenseKey.enabled ? 'disable' : 'enable';

    const { error } = await toggleLicenseKey(subscription.licenseKey._id, action);

    if (error) {
      setIsTogglingLicenseKey(false);
      window.alert(`Failed to ${action} license key. Try again or contact support.`);
      return;
    }

    refetch();

    setIsTogglingLicenseKey(false);
  }

  const subscriptionPeriodStatus = getSubscriptionPeriodStatus(subscription);
  const subscriptionScheduleStatus = getSubscriptionScheduleStatus(subscription);

  const hasFees = subscription.fees?.length > 0;
  const hasRecurringVariants = subscription.variants?.some(
    variant => variant.selectedOptionType === 'recurring',
  );
  const hasOneTimeVariants = subscription.variants?.some(
    variant => variant.selectedOptionType === 'one_time',
  );

  const subscriptionDetails = new Map([
    ['Checkout name', <CheckoutTitle key="checkout" subscription={subscription} />],
    ['Product title', subscription.page?.productTitle || '—'],
    [
      <>
        Subscription amount{' '}
        <InfoTooltip>
          {[
            'Total subscription amount',
            ...(hasRecurringVariants && hasFees
              ? ['including recurring variant options and fees']
              : [
                  ...(hasRecurringVariants ? ['including recurring variant options'] : []),
                  ...(hasFees ? ['including fees'] : []),
                ]),
          ].join(' ')}
        </InfoTooltip>
      </>,
      getPriceLabel({
        amount: subscription.amount,
        currency: subscription.currency,
        interval: subscription.interval,
        intervalCount: subscription.intervalCount,
        type: 'subscription',
      }),
    ],
    ...(subscription.dynamicPrice
      ? [
          [
            'Dynamic product price',
            getPriceLabel({
              amount: subscription.dynamicPrice,
              currency: subscription.currency,
              interval: subscription.interval,
              intervalCount: subscription.intervalCount,
              type: 'subscription',
            }),
          ],
        ]
      : []),
    ...(subscription.productPrice && !subscription.dynamicPrice
      ? [
          [
            'Product price',
            getPriceLabel({
              amount: subscription.productPrice,
              currency: subscription.currency,
              interval: subscription.interval,
              intervalCount: subscription.intervalCount,
              type: 'subscription',
            }),
          ],
        ]
      : []),
    ...(subscription.discountedFromPrice
      ? [
          [
            'Discounted from',
            <span className="line-through" key="discounted-from">
              {getPriceLabel({
                amount: subscription.discountedFromPrice,
                currency: subscription.currency,
                interval: subscription.interval,
                intervalCount: subscription.intervalCount,
                type: 'subscription',
              })}
            </span>,
          ],
        ]
      : []),
    ...(subscription.setupFee
      ? [
          [
            'Setup fee',
            <>
              <div>
                {formatPrice({
                  amount: subscription.setupFee,
                  currency: subscription.currency,
                })}
              </div>

              {subscription.setupFeeDiscountAmount ? (
                <div>
                  -
                  {getPriceLabel({
                    amount: subscription.setupFeeDiscountAmount,
                    currency: subscription.currency,
                  })}{' '}
                  coupon discount
                </div>
              ) : (
                ''
              )}

              {subscription.setupFeeFees
                ? subscription.setupFeeFees.map(fee => (
                    <div key={fee._id}>
                      +
                      {getPriceLabel({
                        amount: fee.amount,
                        currency: subscription.currency,
                      })}{' '}
                      {toLower(fee.name)} ({fee.percentage}%)
                    </div>
                  ))
                : ''}

              {subscription.setupFeeTotal &&
              subscription.setupFee !== subscription.setupFeeTotal ? (
                <div>
                  Total:{' '}
                  {getPriceLabel({
                    amount: subscription.setupFeeTotal,
                    currency: subscription.currency,
                  })}
                </div>
              ) : (
                ''
              )}
            </>,
          ],
        ]
      : []),
    ...(subscription.fees?.length > 0
      ? subscription.fees.map(fee => [
          fee.name,
          `${getPriceLabel({
            amount: fee.amount,
            currency: subscription.currency,
            interval: subscription.interval,
            intervalCount: subscription.intervalCount,
            type: 'subscription',
          })} ${fee.percentage ? `(${fee.percentage}%)` : ''}`,
        ])
      : []),
    [
      'Payment type',
      <div key="payment-type">
        <div>{subscription.planIterations ? 'Payment plan' : 'Subscription'}</div>
        <div className="mt-1">{subscriptionPeriodStatus}</div>
      </div>,
    ],
    ...(subscription.planIterations
      ? [['No. of payments', <div key="no-of-payments">{subscriptionScheduleStatus}</div>]]
      : []),
    ['Created', format(new Date(subscription.createdAt), 'd MMM, yyyy, HH:mm')],
  ]);

  const customerDetails = {
    'Customer name': (subscription.customer && subscription.customer.name) || 'Not provided',
    'Customer email':
      subscription.customer && subscription.customer.email ? (
        <Link to={`mailto:${subscription.customer.email}`} target="_blank" variant="withIcon">
          <EnvelopeIcon className="h-4 w-4 mt-0.5" />

          {subscription.customer.email}
        </Link>
      ) : (
        'Not provided'
      ),
  };

  const details = {
    'Order ID': subscription.orderId,
    'Payment method': subscription.paymentMethod
      ? getPaymentMethodLabel(subscription.paymentMethod)
      : '—',
    SKU: subscription.sku || '—',
    ...(subscription.feeAmount && {
      'Checkout Page fee': formatPrice({
        amount: subscription.feeAmount,
        currency: subscription.currency,
      }),
    }),
    'Customer ID': (subscription.customer && `#${subscription.customer._id}`) || '–',
    'Subscription ID': `#${subscription._id}`,
    'Stripe customer ID': subscription.customer?.stripeCustomerId ? (
      <StripeLinkWrapper
        disabled={!subscription.livemode}
        tooltipLabel="Only for live subscriptions"
      >
        <Link
          blank
          to={getStripeDashboardUrl(`/customers/${subscription.customer.stripeCustomerId}`, {
            seller,
            isAdminLoggedInAsSeller,
          })}
          className={clsx({
            'pointer-events-none opacity-50 cursor-not-allowed': !subscription.livemode,
          })}
          external
        >
          {subscription.customer.stripeCustomerId}
        </Link>
      </StripeLinkWrapper>
    ) : (
      '—'
    ),
    'Stripe subscription ID': subscription.subscriptionId ? (
      <StripeLinkWrapper
        disabled={!subscription.livemode}
        tooltipLabel="Only for live subscriptions"
      >
        <Link
          blank
          to={getStripeDashboardUrl(`/subscriptions/${subscription.subscriptionId}`, {
            seller,
            isAdminLoggedInAsSeller,
          })}
          className={clsx({
            'pointer-events-none opacity-50 cursor-not-allowed': !subscription.livemode,
          })}
          external
        >
          {subscription.subscriptionId}
        </Link>
      </StripeLinkWrapper>
    ) : (
      '–'
    ),
  };

  const queryParameters = subscription.queryParameters
    ? Object.entries(subscription.queryParameters).filter(([name]) => {
        return name !== 'previewKey' && name !== 'testing';
      })
    : [];

  const amountToBeDiscounted = getAmountToBeDiscounted({
    subscriptionAmount: subscription.amount,
    fees: subscription.fees,
  });
  const couponDiscount = getCouponDiscount(amountToBeDiscounted, subscription.coupon);

  return (
    <>
      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          {!isSubscriptionSuccessful(subscription.status) && (
            <div className="px-4">
              <Callout variant="error" padded={false}>
                <Callout.Heading>Subscription failed</Callout.Heading>
                <Callout.Content>{subscription.paymentError}</Callout.Content>
              </Callout>
            </div>
          )}

          {subscription.status === 'canceled' && (
            <div className="px-4">
              <Callout variant="error" padded={false}>
                <Callout.Heading>Subscription canceled</Callout.Heading>
                {subscription.canceledAt != null && (
                  <Callout.Content>{formatDateTime(subscription.canceledAt)}</Callout.Content>
                )}
                {subscription.reason && <Callout.Content>{subscription.reason}</Callout.Content>}
              </Callout>
            </div>
          )}

          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>Subscription</PropertyList.Title>
              {Array.from(subscriptionDetails.entries()).map(([key, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>

          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>
                Customer
                {subscription.customer && (
                  <div className="mt-auto ml-auto">
                    <Link
                      to={`/customers/${subscription.customer._id}`}
                      className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                    >
                      View customer <ArrowRightIcon className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </PropertyList.Title>
              {Object.entries(customerDetails).map(([key, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>

          {subscription.coupon && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Coupon</PropertyList.Title>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Code</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <Link to={`/coupons/${subscription.coupon._id}`}>
                      {subscription.coupon.code}
                    </Link>
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                {subscription.coupon.percentOff && (
                  <PropertyList.Item>
                    <PropertyList.ItemLabel>Discount percentage</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>
                      {subscription.coupon.percentOff}%
                    </PropertyList.ItemValue>
                  </PropertyList.Item>
                )}

                {couponDiscount > 0 && (
                  <PropertyList.Item>
                    <PropertyList.ItemLabel>
                      Discount amount
                      {subscription.coupon.percentOff &&
                        (subscription.setupFee || hasOneTimeVariants) && (
                          <InfoTooltip>
                            Discount on recurring amount before fees, excluding discount on one-time
                            variants and setup fee.
                          </InfoTooltip>
                        )}
                    </PropertyList.ItemLabel>
                    <PropertyList.ItemValue>
                      -
                      {getPriceLabel({
                        amount: couponDiscount,
                        currency: subscription.currency,
                      })}
                    </PropertyList.ItemValue>
                  </PropertyList.Item>
                )}

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Duration</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {subscription.coupon.duration === 'once' && 'Once'}
                    {subscription.coupon.duration === 'forever' && 'Forever'}
                    {subscription.coupon.duration === 'repeating' &&
                      `${subscription.coupon.durationInMonths} months`}
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          {subscription.discount && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Discount</PropertyList.Title>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Discount amount</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {getPriceLabel({
                      amount: subscription.discount.amount,
                      currency: subscription.currency,
                    })}
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Discount percentage</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {subscription.discount.percentOff}%
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          {subscription.variants && subscription.variants.length > 0 ? (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Variants</PropertyList.Title>

                {subscription.variants.map(variant => {
                  if (variant.selectionType === 'single') {
                    return (
                      <PropertyList.Item key={variant._id}>
                        <PropertyList.ItemLabel>{variant.name}</PropertyList.ItemLabel>

                        <PropertyList.ItemValue>
                          <PropertyList.ItemValue>
                            {variant.selectedOptionName}
                          </PropertyList.ItemValue>

                          {variant.sku && (
                            <PropertyList.ItemValue>SKU: {variant.sku}</PropertyList.ItemValue>
                          )}

                          {variant.additionalChargeAmount && (
                            <PropertyList.ItemValue>
                              {getPriceLabel({
                                amount: variant.additionalChargeAmount,
                                type:
                                  variant.selectedOptionType === 'recurring'
                                    ? 'subscription'
                                    : 'one_time',
                                currency: subscription.currency,
                                interval: subscription.interval,
                                intervalCount: subscription.intervalCount,
                              })}
                            </PropertyList.ItemValue>
                          )}

                          {variant.discountAmount && (
                            <PropertyList.ItemValue>
                              -
                              {getPriceLabel({
                                amount: variant.discountAmount,
                                currency: subscription.currency,
                              })}{' '}
                              coupon discount
                            </PropertyList.ItemValue>
                          )}

                          {variant.fees &&
                            variant.fees.map(fee => (
                              <PropertyList.ItemValue key={fee._id}>
                                +
                                {getPriceLabel({
                                  amount: fee.amount,
                                  currency: subscription.currency,
                                })}{' '}
                                {toLower(fee.name)} ({fee.percentage}%)
                              </PropertyList.ItemValue>
                            ))}

                          {variant.additionalChargeAmountTotal &&
                            variant.additionalChargeAmount !==
                              variant.additionalChargeAmountTotal && (
                              <>
                                <PropertyList.ItemValue>
                                  Total:{' '}
                                  {getPriceLabel({
                                    amount: variant.additionalChargeAmountTotal,
                                    currency: subscription.currency,
                                  })}
                                </PropertyList.ItemValue>
                              </>
                            )}
                        </PropertyList.ItemValue>
                      </PropertyList.Item>
                    );
                  }

                  if (
                    variant.selectionType === 'multiple' ||
                    variant.selectionType === 'quantity'
                  ) {
                    return variant.selectedOptions.map((option, index) => (
                      <PropertyList.Item key={index}>
                        <PropertyList.ItemLabel>{variant.name}</PropertyList.ItemLabel>

                        <PropertyList.ItemValue>
                          <PropertyList.ItemValue>
                            <div className="flex">
                              {!option.additionalChargeAmount && (
                                <div className="whitespace-nowrap mr-1">
                                  {option.quantity}
                                  <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                </div>
                              )}

                              <div className="w-2/4">
                                <div>{option.optionName}</div>

                                {option.sku && <div>SKU: {option.sku}</div>}
                              </div>

                              {option.additionalChargeAmount && (
                                <>
                                  <div className="w-1/4 text-right">
                                    <div>
                                      <span className="whitespace-nowrap mr-1">
                                        {option.quantity}
                                        <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                      </span>

                                      <span>
                                        {getPriceLabel({
                                          amount: option.additionalChargeAmount,
                                          type:
                                            option.optionType === 'recurring'
                                              ? 'subscription'
                                              : 'one_time',
                                          currency: subscription.currency,
                                          interval: subscription.interval,
                                          intervalCount: subscription.intervalCount,
                                        })}
                                      </span>
                                    </div>

                                    {option.discountAmount ? (
                                      <div>
                                        -
                                        {getPriceLabel({
                                          amount: option.discountAmount,
                                          currency: subscription.currency,
                                        })}{' '}
                                        coupon discount
                                      </div>
                                    ) : (
                                      ''
                                    )}

                                    {option.fees.length > 0 &&
                                      option.fees.map(fee => (
                                        <div key={fee._id}>
                                          +
                                          {getPriceLabel({
                                            amount: fee.amount,
                                            currency: subscription.currency,
                                          })}{' '}
                                          {toLower(fee.name)} ({fee.percentage}%)
                                        </div>
                                      ))}

                                    {option.additionalChargeAmountTotal &&
                                      option.additionalChargeAmount !==
                                        option.additionalChargeAmountTotal && (
                                        <>
                                          <div>
                                            Total:{' '}
                                            {getPriceLabel({
                                              amount: option.additionalChargeAmountTotal,
                                              currency: subscription.currency,
                                            })}
                                          </div>
                                        </>
                                      )}
                                  </div>

                                  <div className="w-1/4 text-right">
                                    (
                                    {getPriceLabel({
                                      amount: option.additionalChargeAmount * option.quantity,
                                      type:
                                        option.optionType === 'recurring'
                                          ? 'subscription'
                                          : 'one_time',
                                      currency: subscription.currency,
                                      interval: subscription.interval,
                                      intervalCount: subscription.intervalCount,
                                    })}
                                    )
                                  </div>
                                </>
                              )}
                            </div>
                          </PropertyList.ItemValue>
                        </PropertyList.ItemValue>
                      </PropertyList.Item>
                    ));
                  }
                })}
              </CardContent>
            </Card>
          ) : (
            ''
          )}

          {subscription.fields && subscription.fields.length > 0 && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>
                  Fields
                  <div className="mt-auto ml-auto">
                    <FieldEditDialog
                      fields={subscription.fields}
                      page={subscription.page}
                      updateFields={updateFields}
                    />
                  </div>
                </PropertyList.Title>
                {subscription.fields.map(field => (
                  <PropertyList.Item key={field._id}>
                    <PropertyList.ItemLabel>
                      {field.label}
                      <div className="text-gray-500 text-xs font-normal">
                        {getDataFieldTypeLabel(field.type)}
                      </div>
                    </PropertyList.ItemLabel>
                    <PropertyList.ItemValue>
                      <MultilineText>{renderFieldValue(field)}</MultilineText>
                    </PropertyList.ItemValue>
                  </PropertyList.Item>
                ))}
              </CardContent>
            </Card>
          )}

          {subscription.licenseKey && subscription.licenseKey.key && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>License key</PropertyList.Title>
                <PropertyList.Item>
                  <PropertyList.ItemLabel>Key</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {subscription.licenseKey.key}
                    <CopyToClipboard text={subscription.licenseKey.key} className="ml-1">
                      <Button appearance="subtle">
                        <ClipboardIcon width="16px" />
                      </Button>
                    </CopyToClipboard>
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Uses</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{subscription.licenseKey.uses}</PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Status</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <PropertyList.ItemValue>
                      {subscription.licenseKey.enabled ? 'Enabled' : 'Disabled'}
                    </PropertyList.ItemValue>

                    <PropertyList.ItemValue className="inline-flex items-center gap-x-2">
                      <Button
                        appearance="subtle"
                        onClick={handleToggleLicenseKey}
                        disabled={isTogglingLicenseKey}
                      >
                        {subscription.licenseKey.enabled ? 'Disable' : 'Enable'}
                      </Button>

                      {isTogglingLicenseKey && <Spinner width="24px" height="24px" />}
                    </PropertyList.ItemValue>
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>Details</PropertyList.Title>
              {Object.entries(details).map(([key, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>

          {!isEmpty(queryParameters) && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Query parameters</PropertyList.Title>
                {queryParameters.map(([name, value], index) => (
                  <PropertyList.Item key={index}>
                    <PropertyList.ItemLabel>{name}</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                  </PropertyList.Item>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className="lg:border-l lg:border-b-0 border-gray-100 lg:mt-4 lg:pl-8 ml-4 lg:ml-0 pb-4 flex flex-col sm:flex-row whitespace-nowrap
           lg:flex-col gap-x-4 gap-y-2 items-start order-1 lg:order-2 lg:w-[22rem]"
        >
          {subscription.customer && (
            <Link
              to={`/customers/${subscription.customer._id}`}
              variant="withIcon"
              className="w-full gap-x-2"
            >
              <EyeIcon className="h-4 w-4" /> View customer
            </Link>
          )}

          <FieldEditDialog
            fields={subscription.fields}
            page={subscription.page}
            updateFields={updateFields}
            trigger={
              <Link
                className={clsx(
                  'w-full flex gap-x-2 items-center font-medium text-gray-900 cursor-pointer',
                )}
                onClick={() => {}}
              >
                <>
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit fields
                </>
              </Link>
            }
          />

          <Link
            onClick={handleSendSubscriptionConfirmationEmail}
            className={clsx(
              'w-full flex gap-x-2 items-center',
              isSendingSubscriptionConfirmationEmail && 'opacity-50 cursor-not-allowed',
            )}
          >
            <>
              <EnvelopeIcon className="w-4 h-4" />
              Resend confirmation
            </>
            {isSendingSubscriptionConfirmationEmail && <Spinner width="24px" height="24px" />}
          </Link>

          <PaymentsSubscriptionsCancelDialog subscription={subscription} />

          <StripeLinkWrapper
            disabled={!subscription.livemode || !subscription.subscriptionId}
            tooltipLabel="Only for live subscriptions"
            className="w-full"
          >
            <Link
              to={getStripeDashboardUrl(`/subscriptions/${subscription.subscriptionId}`, {
                seller,
                isAdminLoggedInAsSeller,
              })}
              blank
              className={clsx(`w-full flex items-center gap-x-2`, {
                'pointer-events-none opacity-50 cursor-not-allowed':
                  !subscription.livemode || !subscription.subscriptionId,
              })}
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" /> View in Stripe
            </Link>
          </StripeLinkWrapper>
        </div>
      </div>
    </>
  );
};

function StripeLinkWrapper({ disabled, tooltipLabel, ...props }) {
  if (disabled) {
    return (
      <Tooltip side="right" label={tooltipLabel} {...props}>
        <button className="w-full">{props.children}</button>
      </Tooltip>
    );
  } else {
    return <React.Fragment {...props} />;
  }
}

const getSubscriptionScheduleStatus = subscription => {
  if (!subscription.planIterations) return;

  if (
    subscription.status !== 'completed' &&
    subscription.status !== 'canceled' &&
    subscription.cancelAt
  ) {
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')} after ${
      subscription.planIterations
    } payments`;
  }

  return `Ends after ${subscription.planIterations} payments`;
};

const getSubscriptionPeriodStatus = subscription => {
  if (subscription.status === 'canceled') {
    const cancelDate = subscription.cancelAt || subscription.canceledAt || undefined;

    if (cancelDate) {
      return `Canceled on ${format(new Date(cancelDate), 'd MMM, yyyy')}`;
    }

    return 'Canceled';
  }

  if (subscription.cancelAtPeriodEnd) {
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  }

  if (subscription.status === 'scheduled') {
    return `Starts on ${format(new Date(subscription.trialEnd), 'd MMM, yyyy')}`;
  }

  if (subscription.status === 'trialing' && subscription.trialEnd) {
    return `Trial ends on ${format(new Date(subscription.trialEnd), 'd MMM, yyyy')}`;
  }

  if (subscription.status === 'completed') {
    return `Completed on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  }

  if (
    subscription.status !== 'completed' &&
    subscription.status !== 'canceled' &&
    isSameDay(new Date(subscription.cancelAt), new Date(subscription.currentPeriodEnd))
  ) {
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  }

  if (subscription.currentPeriodEnd) {
    return `Renews on ${format(new Date(subscription.currentPeriodEnd), 'd MMM, yyyy')}`;
  }

  return '';
};

function CheckoutTitle({ subscription }) {
  const title = subscription?.page?.name || '—';

  if (!subscription.page?._id) {
    return title;
  }

  return (
    <Link to={`/pages/${subscription.page._id}`} className="font-medium">
      {title}
    </Link>
  );
}

export default PaymentsSubscriptionsDetail;
